import React from 'react';
import PropTypes from 'prop-types';
import { i18n } from '../../localization/i18n';
import logger from '../../Helpers/logger';
import styles from './index.module.scss';

/**
 * Catches a render error below it and shows a fallback instead of unmounting the
 * whole tree above.
 *
 * A chat renders what other people sent and what the backend returned, so one
 * message with an unexpected shape must not blank the screen.
 *
 * @param children The subtree to guard.
 * @param fallback A node, or a function receiving ({ error, retry }).
 * @param context Identifiers logged with the error, such as chatId or messageId.
 * @param onError Called after the error is logged, for extra reporting.
 * @returns The children, or the fallback once an error has been caught.
 */
class ErrorBoundary extends React.Component {
    state = { error: null };

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, errorInfo) {
        const { context = {}, onError } = this.props;
        logger.error('Render error caught by boundary', {
            ...context,
            message: error?.message,
            componentStack: errorInfo?.componentStack,
        });
        onError?.(error, errorInfo);
    }

    /* Remounting the subtree is the only way back: a boundary that has caught an
       error keeps showing the fallback until its state is cleared. */
    handleRetry = () => this.setState({ error: null });

    render() {
        const { error } = this.state;
        const { children, fallback } = this.props;

        if (!error) return children;

        if (typeof fallback === 'function') {
            return fallback({ error, retry: this.handleRetry });
        }
        if (fallback !== undefined) return fallback;

        return (
            <div className={styles.fallback} role="alert">
                <p className={styles.message}>{i18n.t('toast.SOMETHING_WENT_WRONG_RETRY')}</p>
                <button type="button" className={styles.retry} onClick={this.handleRetry}>
                    {i18n.t('common.TRY_AGAIN')}
                </button>
            </div>
        );
    }
}

ErrorBoundary.propTypes = {
    children: PropTypes.node,
    fallback: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    context: PropTypes.object,
    onError: PropTypes.func,
};

export default ErrorBoundary;
